import { LifecycleBeginDetailsPayload } from './../../../shared/interfaces/lifecycle-begin-details.interface';
import {
  ProfileBookingPayload,
  PhotoListResponse,
  ValidateLoyaltyNumberServiceResponse,
  ValidateLoyaltyNumberServicePayload,
  CruiseSurveyRetrievePayload,
  CruiseSurveyRetrieveResponse,
  CruiseSurveySavePayload,
  CruiseSurveySaveResponse
} from './../../../models/action-payloads';
// Angular
import { Injectable } from '@angular/core';

// RXJS
import { Observable, of } from 'rxjs';

// General Settings
import { constants } from '../../../constants';
import { BrandName, EnvironmentName, Language } from '~environments/env.interface';
import { CHANNEL, API_PATH, ENDPOINTS, environment } from '~environments/environment';

// Services
import { HttpRepositoryService } from './http-repository.service';
import { ResourceBundleService } from './resource-bundle.service';

// Interfaces or anemic models
import {
  IRetrieveCardCountryResponse,
  IRetrieveOnboardPaymentPayload,
  IRetrieveOnboardPaymentResponse,
  IProfileBookingPayload,
  ISailingInformationResponse,
  IGuestInformationSavePayload,
  IGuestInformationSaveResponse,
  IRetrieveCheckinStatusRequestPayload,
  IRetrieveCheckinStatusResponse,
  ICreateUpdateOnboardPaymentPayload,
  ICreateUpdateOnboardPaymentResponse,
  ValidTravelDocumentListResponse,
  TravelDocumentListPayload,
  BoardingPassResponse,
  PersonalDetailContentResponse,
  CountryPayload
} from 'src/app/models';
import {ICreditCardBinPayload} from '../../../models/credit-card-bin.payload';
import { TelemetryErrorData, GenericResponse } from '../../../shared/interfaces';
import { IDeleteAuthorizedConsents, IDeleteAuthorizedConsentsResponse } from '../../../models/delete-authorized-consents.payload';
import { IGovernanceContentParams, IGovernanceContentResponse, IHealthCenterProtocolsResponse } from 'src/app/models/governance-content.payload';
import { ITravelQuestionnairePayload, ITravelQuestionnaireResponse, ISaveTravelQuestionnairePayload } from '../../../models/travel-questionnaire.payload';
import { IAuthorizedConsentsPayload, IAuthorizedConsentsResponse, IAuthorizedConsentsSavePayload, IAuthorizedConsentsSaveResponse } from '../../../models/authorized-consents.payload';
import { HttpParams } from "@angular/common/http";
import { ISavePassengerSurveyPayload } from 'src/app/models/save-passenger-survey.payload';

@Injectable()
export class MicroServicesService {
  constructor(
    private readonly httpRepositoryService: HttpRepositoryService,
    private readonly resourceBundleService: ResourceBundleService,
  ) { }

  // Support accessors
  get apiUrl(): string {
    return API_PATH;
  }

  get brand(): string {
    return this.httpRepositoryService.brand;
  }

  get vdsId(): string {
    return this.httpRepositoryService.vdsId;
  }

  get locale(): string {
    return this.httpRepositoryService.locale;
  }

  get endpoints(): {[key: string]: string} {
    return ENDPOINTS;
  }

  get channel(): string {
    return CHANNEL;
  }

  public getSetSailPass(payload: any): Observable<BoardingPassResponse> {
    const body = {
      grantor: {
        guestAccountId: this.vdsId
      },
      grantees: payload,
      source: constants.SOURCE
    };

    const telemetryErrorData: TelemetryErrorData = {
      message: `Getting sail pass, user:${this.vdsId}`,
      lookup: 'GC-027-POST-BOARDING-PASS-FAILED'
    }

    const url = `${this.apiUrl}/${this.locale}/${this.brand}${this.endpoints.API_MICROSERVICE_BOARDINGPASS}/${this.vdsId}`;
    return this.httpRepositoryService.post<BoardingPassResponse>(url, body, telemetryErrorData);
  }

  public getArrivalAppointmentsAndInventory(payload: any): Observable<any> {
    const telemetryErrorData: TelemetryErrorData = {
      message: `Getting arrival appointment and inventory, user:${this.vdsId}`,
      lookup: 'GC-028-GET-ARRIVAL-APPOINTMENT-FAILED'
    }
    // tslint:disable-next-line: max-line-length
    const url = `${this.apiUrl}/${this.locale}/${this.brand}${ENDPOINTS.API_MICROSERVICE_ARRIVAL_APPOINTMENTS}/${this.vdsId}${ENDPOINTS.API_MICROSERVICE_ARRIVAL_INVENTORY}`;
    return this.httpRepositoryService.post(url, payload, telemetryErrorData);
  }

  public deleteAuthorizedConsents(payload: IDeleteAuthorizedConsents): Observable<IDeleteAuthorizedConsentsResponse> {
    const url = `${this.apiUrl}/${this.locale}/${this.brand}${ENDPOINTS.API_MICROSERVICE_DELETE_AUTHORIZED_CONSENTS}/${this.vdsId}`;
    const telemetryErrorData: TelemetryErrorData = {
      message: `Getting guest delete authorized consents, user:${this.vdsId}`,
      lookup: 'GC-012-POST-AUTHORIZED-CONSENTS_FAILED'
    }
    return this.httpRepositoryService.post<IDeleteAuthorizedConsentsResponse>(url, payload, telemetryErrorData);
  }

  public setOrUpdateArrivalTime(payload: any): Observable<any> {
    const telemetryErrorData: TelemetryErrorData = {
      message: `Setting/Update arrival appointment and inventory, user:${this.vdsId}`,
      lookup: 'GC-029-POST-ARRIVAL-APPOINTMENT-FAILED'
    }
    const url = `${this.apiUrl}/${this.locale}/${this.brand}${ENDPOINTS.API_MICROSERVICE_ARRIVAL_APPOINTMENTS}/${this.vdsId}`;
    return this.httpRepositoryService.post(url, payload, telemetryErrorData);
  }

  public getProfileBookings({brand, shipCode, sailDate}: IProfileBookingPayload): Observable<GenericResponse<ProfileBookingPayload>> {
    const queryParams = new HttpParams()
      .set('brand', brand)
      .set('shipCode', shipCode)
      .set('sailDate', sailDate)
      .set('includeCheckin', true);

    const url = `${this.apiUrl}${ENDPOINTS.API_MICROSERVICE_PROFILE_BOOKINGS}/${this.vdsId}`;
    const telemetryErrorData: TelemetryErrorData = {
      message: `Get profile bookings for ${this.vdsId} failed`,
      lookup: 'GC-008-GET-PROFILE-BOOKINGS-FAILED'
    }
    return this.httpRepositoryService.get<GenericResponse<ProfileBookingPayload>>(url, telemetryErrorData, false, queryParams);
  }

  public getResourceBundle(): Observable<any> {
      const resourceBundleObject = {
        resourceBundle: this.resourceBundleService.getResourceBundle(environment?.envName as EnvironmentName, this.brand as BrandName, this.locale as Language ?? 'en'),
        locale: this.locale,
      }
      return of(resourceBundleObject);
  }

  public getBeginDetails(payload: any): Observable<GenericResponse<LifecycleBeginDetailsPayload>> {
    const url = `${this.apiUrl}/${this.locale}/${this.brand}${ENDPOINTS.API_MICROSERVICE_BEGIN_DETAILS}/${this.vdsId}`;
    const telemetryErrorData: TelemetryErrorData = {
      message: `Getting guest details information for the selected guests failed , user:${this.vdsId}`,
      lookup: 'GC-009-GET-SELECTED-GUEST-DETAILS-FAILED'
    }
    return this.httpRepositoryService.post(url, payload, telemetryErrorData);
  }

  public guestCheckinEnd(payload: any): Observable<any> {
    const url = `${this.apiUrl}/${this.locale}/${this.brand}${ENDPOINTS.API_MICROSERVICE_GUEST_END_SESSION}/${this.vdsId}`;
    const telemetryErrorData: TelemetryErrorData = {
      message: `Unexpected error sending microservice guest lifecycle end`,
      lookup: 'GC-004-POST-LIFECYCLE-END-FAILED'
    }
    return this.httpRepositoryService.post(url, payload, telemetryErrorData);
  }

  public getRetrieveOnboardPayment(payload: IRetrieveOnboardPaymentPayload): Observable<IRetrieveOnboardPaymentResponse> {
    const body = payload;
    // tslint:disable-next-line: max-line-length
    const url = `${this.apiUrl}/${this.locale}/${this.brand}/${CHANNEL}${ENDPOINTS.API_MICROSERVICE_RETRIEVE_ONBOARD_PAYMENT}/${this.vdsId}${ENDPOINTS.API_MICROSERVICE_SEARCH}`;

    const telemetryErrorData: TelemetryErrorData = {
      message: `Unexpected error getting onboard expenses`,
      lookup: 'GC-025-POST-ONBOARD-PAYMENT-FAILED'
    }

    return this.httpRepositoryService.post<IRetrieveOnboardPaymentResponse>(url, body, telemetryErrorData);
  }

  public getRetrieveCreditCardCountry({ bin }: ICreditCardBinPayload): Observable<IRetrieveCardCountryResponse> {
    const url = `${this.apiUrl}/${this.locale}/${this.brand}/${ENDPOINTS.API_MICROSERVICE_RETRIEVE_CARD_COUNTRY}/${bin}`;
    const telemetryErrorData: TelemetryErrorData = {
      message: `Unexpected error getting credit card country`,
      lookup: 'GC-032-GET-CREDIT-CARD-COUNTRY-FAILED'
    }

    return this.httpRepositoryService.get<IRetrieveCardCountryResponse>(url, telemetryErrorData);
  }

  public createUpdateOnboardPayment(
    payload: ICreateUpdateOnboardPaymentPayload,
    url: string,
    telemetryErrorData: TelemetryErrorData
  ): Observable<ICreateUpdateOnboardPaymentResponse> {
    return this.httpRepositoryService.post<ICreateUpdateOnboardPaymentResponse>(url, payload, telemetryErrorData);
  }

  public getSailingInformation(voyages: string): Observable<ISailingInformationResponse> {
    const url = `${this.apiUrl}/${this.locale}/${this.brand}/${CHANNEL}${ENDPOINTS.API_MICROSERVICE_SAILING_INFORMATION_ENRICHED}/${voyages}${ENDPOINTS.API_MICROSERVICE_ENRICHED}`;
    const telemetryErrorData: TelemetryErrorData = {
      message: 'Get sailing information enriched failed',
      lookup: 'GC-007-GET-SAILING-INFORMATION-ENRICHED-FAILED'
    }
    return this.httpRepositoryService.get<ISailingInformationResponse>(url, telemetryErrorData);
  }

  public getCheckinStatus(payload: IRetrieveCheckinStatusRequestPayload): Observable<IRetrieveCheckinStatusResponse> {
    const body = payload;
    // tslint:disable-next-line: max-line-length
    const url = `${this.apiUrl}/${this.locale}/${this.brand}/${CHANNEL}${ENDPOINTS.API_MICROSERVICE_RETRIEVE_CHECKIN_STATUS}/${this.vdsId}`;
    const telemetryErrorData: TelemetryErrorData = {
      message: 'Get guests status for each required section, page failed',
      lookup: 'GC-011-POST-GUEST-STATUSES-FAILED'
    }
    return this.httpRepositoryService.post<IRetrieveCheckinStatusResponse>(url, body, telemetryErrorData);
  }

  public getTravelQuestionnaire(payload: ITravelQuestionnairePayload): Observable<ITravelQuestionnaireResponse> {
    const url = `${this.apiUrl}/${this.locale}/${this.brand}/${CHANNEL}${ENDPOINTS.API_MICROSERVICE_RETRIEVE_TRAVEL_QUESTIONNAIRE}/${this.vdsId}`;
    const telemetryErrorData: TelemetryErrorData = {
      message: 'Getting travel questionnaire failed.',
      lookup: 'GC-020-POST-GET-TRAVEL-QUESTIONNAIRE-FAILED'
    }
    return this.httpRepositoryService.post<ITravelQuestionnaireResponse>(url, payload, telemetryErrorData);
  }

  public createUpdateTravelQuestionnaire(payload: ISaveTravelQuestionnairePayload): Observable<ITravelQuestionnaireResponse> {
    const url = `${this.apiUrl}/${this.locale}/${this.brand}/${CHANNEL}${ENDPOINTS.API_MICROSERVICE_CREATE_UPDATE_TRAVEL_QUESTIONNAIRE}/${this.vdsId}`;
    const telemetryErrorData: TelemetryErrorData = {
      message: 'Creating/Updating travel questionnaire',
      lookup: 'GC-010-POST-TRAVEL-QUESTIONNAIRE-FAILED'
    }
    return this.httpRepositoryService.post<ITravelQuestionnaireResponse>(url, payload, telemetryErrorData);
  }

  public getAuthorizedConsentsAll(payload: IAuthorizedConsentsPayload): Observable<IAuthorizedConsentsResponse> {
    const url = `${this.apiUrl}/${this.locale}/${this.brand}/${CHANNEL}${ENDPOINTS.API_MICROSERVICE_RETRIEVE_AUTHORIZED_CONSENTS}/${this.vdsId}`;
    return this.httpRepositoryService.post<IAuthorizedConsentsResponse>(url, payload);
  }

  public createUpdateAuthorizedConsents(payload: IAuthorizedConsentsSavePayload): Observable<IAuthorizedConsentsSaveResponse> {
    const url = `${this.apiUrl}/${this.locale}/${this.brand}/${CHANNEL}${ENDPOINTS.API_MICROSERVICE_CREATE_UPDATE_AUTHORIZED_CONSENTS}/${this.vdsId}`;
    const telemetryErrorData: TelemetryErrorData = {
      message: 'Creating/Updating authorized consents',
      lookup: 'GC-030-POST-AUTHORIZED-CONSENTS-FAILED'
    }
    return this.httpRepositoryService.post<IAuthorizedConsentsSaveResponse>(url, payload, telemetryErrorData);
  }

  public getGovernanceContent(params: IGovernanceContentParams): Observable<IGovernanceContentResponse> {
    const url = `${this.apiUrl}/${this.locale}/${this.brand}${ENDPOINTS.API_GOVERNANCE_CONTENT}/${params.shipCode}/${params.sailDate}`;
    const telemetryErrorData: TelemetryErrorData = {
      message: 'Get governance content failed ',
      lookup: 'GC-021-GET-GOVERNANCE-CONTENT-FAILED'
    }
    return this.httpRepositoryService.get<IGovernanceContentResponse>(url, telemetryErrorData);
  }

  public getHealthCenterProtocols(params: IGovernanceContentParams): Observable<IHealthCenterProtocolsResponse> {
    const url = `${this.apiUrl}/${this.locale}/${this.brand}${ENDPOINTS.API_HEALTH_CENTER_PROTOCOLS}/${params.shipCode}/${params.sailDate}`;

    return this.httpRepositoryService.get<IHealthCenterProtocolsResponse>(url);
  }

  public createUpdateGuestInformation(payload: IGuestInformationSavePayload): Observable<IGuestInformationSaveResponse> {
    const url = `${this.apiUrl}/${this.locale}/${this.brand}/${CHANNEL}${ENDPOINTS.API_MICROSERVICE_CREATE_UPDATE_GUEST_INFORMATION}/${this.vdsId}`;
    const telemetryErrorData: TelemetryErrorData = {
      message: 'Creating/Updating guest information',
      lookup: 'GC-034-POST-GUEST-INFORMATION-FAILED'
    }
    return this.httpRepositoryService.post<IGuestInformationSaveResponse>(url, payload, telemetryErrorData);
  }

  /**
   * Call to cover the new implementation for valid travel document list endpoint:
   * /v1/guestReservation/guestInformation/validTravelDocuments
   *
   * @param payload - required payload to get the valid document types
   * @returns Observable<ValidTravelDocumentListResponse> with the right response
   * */
  retrieveValidTravelDocumentList(
    payload: TravelDocumentListPayload
  ): Observable<ValidTravelDocumentListResponse> {
    const url = `${this.apiUrl}/${this.locale}/${this.brand}/${CHANNEL}${ENDPOINTS.API_GET_VALID_TRAVEL_DOCUMENTS}`
    const telemetryErrorData: TelemetryErrorData = {
      message: 'Retrieving valid travel document list',
      lookup: 'GC-035-POST-RETRIEVE-VALID-TRAVEL-DOCUMENTS'
    }
    return this.httpRepositoryService.post<ValidTravelDocumentListResponse>(
      url,
      payload,
      telemetryErrorData
    );
  }

  /**
   * Call to cover the new implementation for save passenger survey:
   * /v1/guestReservation/guestInformation/passengerSurvey
   *
   * @param payload - required payload to get the save passenger survey
   * @returns Observable<ISavePassengerSurveyPayload> with the right response
   * */
  public savePassengerSurvey(payload: any): Observable<ISavePassengerSurveyPayload> {
    const url = `${this.apiUrl}/${this.locale}/${this.brand}/${CHANNEL}${ENDPOINTS.API_MICROSERVICE_POST_SURVEY}`;
    const telemetryErrorData: TelemetryErrorData = {
      message: 'Saving passenger survey information',
      lookup: 'GC-036-SAVE-PASSENGER-SURVEY-FAILED'
    }
    return this.httpRepositoryService.post(url, payload, telemetryErrorData);
  }

  /**
   * function to cover the new implementation for get the photo list
   * /v1/guestCheckin/photo/list
   *
   * @param payload - required payload to get the photo list
   * @returns Observable<any> with the right response
   * */
  public retrieveGuestsSecurityPhotoList(payload: any): Observable<PhotoListResponse> {
    const url = `${this.apiUrl}/${this.locale}/${this.brand}/${CHANNEL}${ENDPOINTS.API_MICROSERVICE_GET_SECURITY_PHOTO_LIST}`;
    const telemetryErrorData: TelemetryErrorData = {
      message: 'Get security photo list failed ',
      lookup: 'GC-006-GET-SECURITY-PHOTO-LIST-FAILED'
    }
    return this.httpRepositoryService.post<PhotoListResponse>(url, payload, telemetryErrorData);
  }

  /**
   * function to cover the new implementation for save the photo
   * /v1/guestCheckin/photo/encoded
   *
   * @param payload - required payload to save the photo
   * @returns Observable<any> with the right response
   * */
  public saveGuestSecurityPhoto(payload: any): Observable<any>{
    const url = `${this.apiUrl}/${this.locale}/${this.brand}/${CHANNEL}${ENDPOINTS.API_MICROSERVICE_POST_SECURITY_PHOTO}`;
    const telemetryErrorData: TelemetryErrorData = {
      message: 'Post to save security photo failed ',
      lookup: 'GC-016-POST-SAVE-SECURITY-PHOTO-FAILED'
    }

    return this.httpRepositoryService.post(url, payload, telemetryErrorData);
  }

  /**
   * function to cover the new implementation for update the photo
   * /v1/guestCheckin/photo/encoded
   *
   * @param payload - required payload to update the photo
   * @returns Observable<any> with the right response
   * */
  public updateGuestSecurityPhoto(payload: any): Observable<any>{
    const url = `${this.apiUrl}/${this.locale}/${this.brand}/${CHANNEL}${ENDPOINTS.API_MICROSERVICE_POST_UPDATE_SECURITY_PHOTO}`;
    const telemetryErrorData: TelemetryErrorData = {
      message: 'Post to update security photo failed ',
      lookup: 'GC-017-POST-UPDATE-SECURITY-PHOTO-FAILED'
    }

    return this.httpRepositoryService.put(url, payload, telemetryErrorData);
  }

  public getPersonalDetailContent(): Observable<PersonalDetailContentResponse> {
    const url = `${this.apiUrl}/${this.locale}/${this.brand}/${CHANNEL}${ENDPOINTS.API_MICROSERVICE_GET_PERSONAL_DETAIL_CONTENT}`;
    const telemetryErrorData: TelemetryErrorData = {
      message: 'Get personal detail content failed',
      lookup: 'GC-005-GET-PERSONAL-DETAIL-CONTENT-FAILED'
    };
    return this.httpRepositoryService.get<PersonalDetailContentResponse>(url, telemetryErrorData);
  }

  public validateLoyaltyNumber(payload: ValidateLoyaltyNumberServicePayload): Observable<ValidateLoyaltyNumberServiceResponse | null> {
    const url = `${this.apiUrl}/${this.locale}/${this.brand}/${CHANNEL}${ENDPOINTS.API_MICROSERVICE_POST_VALIDATE_LOYALTY}`;
    const telemetryErrorData: TelemetryErrorData = {
      message: 'Post request to validate loyalty number failed',
      lookup: 'GC-015-POST-VALIDATE-LOYALTY-NUMBER-FAILED',
    }
    return this.httpRepositoryService.post<ValidateLoyaltyNumberServiceResponse | null>(url, payload, telemetryErrorData);
  }

  public postRetrieveCruiseSurvey(payload: CruiseSurveyRetrievePayload): Observable<GenericResponse<CruiseSurveyRetrieveResponse>> {
    const url = `${this.apiUrl}/${this.locale}/${this.brand}/${CHANNEL}${ENDPOINTS.API_MICROSERVICE_POST_RETRIEVE_CRUISE_SURVEY}`;
    const telemetryErrorData: TelemetryErrorData = {
      message: 'Post retrieve cruise survey failed',
      lookup: 'GC-038-POST-RETRIEVE-CRUISE-SURVEY-FAILED',
    }
    return this.httpRepositoryService.post(url, payload, telemetryErrorData);
  }

  public saveCruiseSurvey(payload: CruiseSurveySavePayload): Observable<GenericResponse<CruiseSurveySaveResponse>> {
    const url = `${this.apiUrl}/${this.locale}/${this.brand}/${CHANNEL}${ENDPOINTS.API_MICROSERVICE_POST_SAVE_CRUISE_SURVEY}`;
    const telemetryErrorData: TelemetryErrorData = {
      message: 'Post save cruise survey failed',
      lookup: 'GC-039-POST-SAVE-CRUISE-SURVEY-FAILED',
    }
    return this.httpRepositoryService.post(url, payload, telemetryErrorData);
  }

  public getCountries(): Observable<GenericResponse<CountryPayload>> {
    const url = `${this.apiUrl}/${this.locale}/${this.brand}/${CHANNEL}${ENDPOINTS.API_MICROSERVICE_GET_COUNTRIES}`;
    const telemetryErrorData: TelemetryErrorData = {
      message: 'Get countries failed',
      lookup: 'GC-037-GET-COUNTRIES-FAILED'
    };
    return this.httpRepositoryService.get<CountryPayload>(url, telemetryErrorData);
  }

}